'use client'
import { useTranslate } from '@repo/shared/hooks'
import { Row, Col, Typography, Input, Skeleton } from '@repo/ui/components'
import type { OrderBoxSummaryProps, OrderBoxMode } from './order-box.types'
import { useState, useEffect } from 'react'
import {
  formatNumberWithCommas,
  formatNumberWithCommasAndDecimals,
  removeCommasFromNumber,
} from '@repo/tools'
import { InformationDialog, PaymentType, type InputCurrency } from '..'

interface OrderBoxSummaryAllProps extends OrderBoxSummaryProps {
  mode: OrderBoxMode
  feeCurrency: string
  targetScale?: number
  kind?: string
}

export const OrderBoxSummary = (props: OrderBoxSummaryAllProps) => {
  const {
    total,
    feeCurrency,
    fee,
    onTotalChange,
    isEditable = false,
    tab,
    isLoadingFee = false,
    token,
    targetScale,
    kind,
  } = props

  const { t } = useTranslate()
  const [inputValue, setInputValue] = useState(
    total ? (total / 10).toLocaleString() : '',
  )
  const shouldHideFee = tab === 'sell' && token === 'MALEK'

  // Update input value when total prop changes
  useEffect(() => {
    const temp = total / (targetScale || 1)
    const val =
      (tab === 'buy' ? temp + fee : tab === 'sell' ? temp - fee : temp) / 10
    setInputValue(total ? formatNumberWithCommasAndDecimals(val, 0) : '')
  }, [total])
  return (
    <Col className='gap-2xs'>
      {!shouldHideFee && (
        <Row className='justify-between mb-xl'>
          <Row className='gap-2xs'>
            <Typography size='xs' color='text-neutral-surface-80'>
              {t('order_fee')}
            </Typography>
            <InformationDialog title={t('order_fee')}>
              {t(`info_${tab}_fee`)}
            </InformationDialog>
          </Row>
          <Row className='gap-2xs w-fit max-h-[24px] min-h-[24px]'>
            {isLoadingFee ? (
              <Skeleton className='h-4 w-16 my-2xs' />
            ) : (
              <Typography
                size='xs'
                color='text-neutral-surface-80'
                className='relative min-h-[22px]'
              >
                {fee ? (
                  <PaymentType
                    isBold={false}
                    value={fee}
                    inputCurrency={feeCurrency as InputCurrency}
                    // className='line-through'
                  />
                ) : (
                  <div className='max-h-[24px] min-h-[24px] flex items-center'>
                    -
                  </div>
                )}
              </Typography>
            )}
            {/* ←
          <Typography size='xs' color='text-neutral-surface-80'>
            {fee ? <PaymentType value={fee} /> : '-'}
          </Typography> */}
          </Row>
        </Row>
      )}

      <Row className='justify-between'>
        <Typography size='xs' weight='bold'>
          {t('order_total')}
        </Typography>
      </Row>
      <Input
        value={inputValue}
        readOnly={!isEditable}
        disabled={!isEditable}
        inputSize='md'
        endLabel={t('currency_toman')}
        className='text-neutral-surface-100 font-bold text-center'
        onChange={(e) => {
          const newValue = e.target.value
          // Remove existing commas and format with new commas
          const cleanValue = removeCommasFromNumber(newValue)
          const formattedValue = formatNumberWithCommas(cleanValue)
          setInputValue(
            feeCurrency === 'IRR'
              ? formattedValue
              : Number(formattedValue).toFixed(),
          )

          if (isEditable && onTotalChange) {
            const numericValue = parseFloat(cleanValue) * 10
            if (!isNaN(numericValue)) {
              onTotalChange(numericValue)
            }
          }
        }}
      />
    </Col>
  )
}
